<template>
  <a-page
    :id="app_id"
    :image_source="image_source"
    :image_height="image_height"
  >
    <template v-slot:header_options>
      <v-select
        v-if="!isGroup"
        v-model="calendar_year"
        :items="available_years.reverse()"
        mandatory
        rounded
        outlined
        dense
        class="mt-8"
        style="max-width: 120px;"
      ></v-select>
    </template>
    <a-hotel-selector-page
      ref="a-hotel-selector-page"
      v-if="isGroup"
    ></a-hotel-selector-page>
    <div v-else>
      <!-- KPIS -->
      <div
        class="d-flex justify-center align-center text-center white--text mt-4 mb-12"
        style="position:relative"
        v-if="ready"
      >
        <div class="px-10 mx-5">
          <span class="text-h1 d-block bold">
            {{ opened_days }}
          </span>
          <span class="text-h5 d-block subtitle">{{ $t("open_days") }}</span>
        </div>
        <div class="px-10 mx-5">
          <span class="text-h1 d-block">{{ total_hotel_events }}</span>
          <span class="text-h5 d-block">{{ $t("events") }}</span>
        </div>
        <div class="px-10 mx-5">
          <span class="text-h1 d-block">{{ getHotelCalendars.length }}</span>
          <span class="text-h5 d-block">{{ $t("calendars") }}</span>
        </div>
        <div class="px-10 mx-5">
          <span class="text-h1 d-block">{{ total_hotel_day_comments }}</span>
          <span class="text-h5 d-block">{{ $t("days_with_reviews") }}</span>
        </div>
      </div>
      <!-- end PAGE HEADER -->
      <v-tabs
        class="px-1"
        background-color="transparent"
        slider-size="2"
        v-model="tab"
        dark
        grow
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab>
          {{ $t("opening_calendar") }}
        </v-tab>
        <v-tab>
          {{ $t("comparative_dates") }}
        </v-tab>
        <v-tab>
          {{ $t("events") }}
        </v-tab>
        <v-tab>
          {{ $t("reviews") }}
        </v-tab>
        <v-tab v-if="isAgency">
          {{ $t("subscriptions") }}
        </v-tab>
      </v-tabs>
      <v-card>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card-text>
              <calendar-openings
                v-if="ready_comparatives_dates"
              ></calendar-openings>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <comparative-dates></comparative-dates>
          </v-tab-item>
          <v-tab-item>
            <v-toolbar color="white" light flat>
              <v-toolbar-title>
                <span>{{ $t("hotel_events") }}</span>
                <span class="font-weight-bold ml-2">
                  {{ hotel_name }}
                </span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <calendar-card-list
                    v-if="current_hotel"
                    :hotel_id="current_hotel.id"
                  ></calendar-card-list>
                </v-col>
                <v-col cols="12" md="9">
                  <calendar-events></calendar-events>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-toolbar color="white" light flat>
              <v-toolbar-title>
                {{ $t("hotel_comments") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <calendar-comments></calendar-comments>
            </v-card-text>
          </v-tab-item>
          <v-tab-item v-if="isAgency">
            <v-toolbar color="white" light flat>
              <v-toolbar-title>
                {{ $t("hotel_calendar_subscriptions") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <hotel-subscribed-calendars
                @getCalendars="getCalendars"
              ></hotel-subscribed-calendars>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </a-page>
</template>

<script>
import aPage from "@/components/aPage";
import CalendarCardList from "@/components/calendar/CalendarCardList.vue";
import CalendarComments from "@/components/calendar/CalendarComments.vue";
import CalendarEvents from "@/components/calendar/CalendarEvents.vue";
import CalendarOpenings from "@/components/calendar/CalendarOpenings.vue";
import ComparativeDates from "@/components/calendar/ComparativeDates.vue";
import HotelSubscribedCalendars from "@/components/calendar/HotelSubscribedCalendars.vue";
import aHotelSelectorPage from "@/components/selector/aHotelSelectorPage";
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "Calendar",
  components: {
    aPage,
    CalendarCardList,
    CalendarComments,
    CalendarEvents,
    HotelSubscribedCalendars,
    ComparativeDates,
    CalendarOpenings,
    aHotelSelectorPage
  },
  data: () => ({
    app_id: "calendar-app",
    image_source: "/img/header-2.jpg", // Mandatory path
    image_height: 380, // Mandatory number
    tab: 0,
    calendar_selected: null
  }),
  computed: {
    isGroup() {
      return this.current_hotel && this.current_hotel.children_number > 0;
    },
    ready() {
      return this.calendar_year && this.current_hotel && this.current_hotel.id;
    },
    ready_comparatives_dates() {
      return (
        this.ready &&
        Object.keys(this.calendar_store.hotel_comparative_dates).length > 0
      );
    },
    total_hotel_events() {
      return this.calendar_store.hotel_events.length;
    },
    total_hotel_day_comments() {
      return this.calendar_store.hotel_comments
        ? this.calendar_store.hotel_comments
            .map(c => c.date)
            .filter((c, index, self) => {
              return self.indexOf(c) === index;
            }).length
        : 0;
    },
    hotel_name() {
      return this.current_hotel && this.current_hotel.name
        ? this.current_hotel.name
        : "";
    },
    isAgency() {
      return (
        this.current_user && this.current_user.permissions.eventsmanagement
      );
    },
    calendar_year: {
      get() {
        return this.calendar_store.calendar_year;
      },
      set(value) {
        this.$store.dispatch("setCalendarYear", value);
      }
    },
    visible_calendars: {
      get() {
        return this.calendar_store.visible_calendars;
      },
      set(value) {
        this.$store.dispatch("setVisibleCalendars", value);
      }
    },
    ...mapState({
      calendar_store: state => state.calendar
    }),
    ...mapGetters({
      current_user: "current_user",
      current_hotel: "current_hotel",
      available_years: "getHotelAvailableYears",
      opened_days: "opened_days",
      getHotelCalendars: "getHotelCalendars"
    })
  },
  methods: {
    getCalendars() {
      this.$store
        .dispatch("getCalendars", {})
        .then(() => {
          this.visible_calendars = this.getHotelCalendars;
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelEvents() {
      this.$store
        .dispatch("getHotelEvents", {
          hotel_id: this.current_hotel.id,
          year: this.calendar_year
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelComments() {
      this.$store
        .dispatch("getHotelComments", {
          hotel_id: this.current_hotel.id,
          year: this.calendar_year
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getOpeningPeriod() {
      this.$store
        .dispatch("getOpeningPeriod", {
          hotel_id: this.current_hotel.id,
          year: this.calendar_year
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelComparativeDates() {
      this.$store
        .dispatch("getHotelComparativeDates", {
          hotel_id: this.current_hotel.id,
          year: this.calendar_year,
          from_date: Vue.moment(this.calendar_year)
            .startOf("year")
            .format("YYYY-MM-DD"),
          to_date: Vue.moment(this.calendar_year)
            .endOf("year")
            .format("YYYY-MM-DD")
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelYearData() {
      this.getOpeningPeriod();
      this.getHotelEvents();
      this.getHotelComments();
      this.getCalendars();
      this.getHotelComparativeDates();
    }
  },
  watch: {
    ready(v) {
      if (v) {
        this.getHotelYearData();
      }
    },
    calendar_year() {
      if (this.ready) {
        this.getHotelYearData();
      }
    }
  },
  mounted() {
    if (this.calendar_year !== null && this.ready) {
      this.getHotelYearData();
    } else {
      this.calendar_year = Vue.moment()
        .year()
        .toString();
    }
  }
};
</script>
